import { Button, ButtonProps, Tooltip } from "@mui/material";
import { FileDownload } from "@mui/icons-material";

import { useAppContext } from "../../../hooks/useAppContext";
import { useCallback } from "react";

// 1. Import the Autonomous Brain
import { useKernel } from "../../../hooks/AIKernel"; // Adjust relative path as needed

const VideoDownloadButton: React.FC<ButtonProps> = ({ ...buttonProps }) => {
  const { isProcessing, videoRef } = useAppContext();

  // 2. Connect to the Agent's state and sensory inputs
  const { agentState, reportBehavior } = useKernel();

  // 3. Adaptive Theme Variables
  const isDarkMode = agentState?.theme === "theme-cinematic-dark";
  const highlightCTA = agentState?.ui?.highlightCTAs;

  const handleDownload = useCallback(() => {
    if (!videoRef || !videoRef.src) {
      console.error("No video source found!");
      return;
    }

    // TELEMETRY: The ultimate success metric. Downloading means the editing session was fruitful.
    if (reportBehavior) {
      reportBehavior("video_download_success", 20, "interest");
    }

    // Create a link and set the URL to the video source
    const link = document.createElement("a");
    link.href = videoRef.src;
    link.download = "edited_video.mp4";

    // Trigger the download
    document.body.appendChild(link);
    link.click();

    // Clean up
    document.body.removeChild(link);
  }, [videoRef, reportBehavior]);

  return (
    <Tooltip arrow disableFocusListener placement="top" title="Download video">
      <span>
        {" "}
        <Button
          {...buttonProps}
          disabled={isProcessing}
          sx={{
            minWidth: "32px",
            // ADAPTIVE UI: Pulse the download button if the user is struggling but has a finished video ready
            animation: highlightCTA && !isProcessing ? "pulse-download 2s infinite" : "none",
            backgroundColor: isDarkMode ? "#00e5ff" : "primary.main",
            color: isDarkMode ? "#000" : "#fff",
            "&:hover": {
              backgroundColor: isDarkMode ? "#00b3cc" : "primary.dark",
            },
            "@keyframes pulse-download": {
              "0%": { boxShadow: "0 0 0 0 rgba(0, 229, 255, 0.7)" },
              "70%": { boxShadow: "0 0 0 10px rgba(0, 229, 255, 0)" },
              "100%": { boxShadow: "0 0 0 0 rgba(0, 229, 255, 0)" }
            }
          }}
          size="small"
          variant="contained"
          onClick={handleDownload}
        >
          <FileDownload />
        </Button>
      </span>
    </Tooltip>
  );
};

export default VideoDownloadButton;